import { motion, AnimatePresence } from "motion/react";
import {
  Activity,
  CheckCircle2,
  AlertTriangle,
  Brain,
  Loader2,
  PenLine,
  Sparkles,
  Wrench,
  XCircle,
} from "lucide-react";
import { Card } from "./Card";
import { formatHHMMSS } from "@/lib/format";

/**
 * HeartbeatEventLog — timeline de eventos do heartbeat run.
 *
 * Genérico pra qualquer entidade que tenha runs com eventos. Classifica
 * eventTypes em ícone + cor + label legível em pt-BR. Usado em Execucao
 * mas reusável em qualquer drawer de "atividade do agente".
 */

export interface LoggedEvent {
  id: string;
  eventType: string;
  message?: string | null;
  createdAt: string;
}

function classifyEvent(ev: LoggedEvent): {
  label: string;
  icon: React.ComponentType<{ size?: number; strokeWidth?: number; color?: string }>;
  color: string;
} {
  const t = ev.eventType.toLowerCase();

  // Eventos parseados do stream-json do Claude (vindos de claude-stream-parser.ts).
  // São os eventos LÚDICOS reais — substituem o que era animação fake antes.
  if (t === "agent.tool_use") {
    return {
      label: ev.message ?? "Usando ferramenta",
      icon: Wrench,
      color: "var(--nd-blue, #4A6FA8)",
    };
  }
  if (t === "agent.tool_result") {
    const isError = ev.message?.startsWith("Falhou:") ?? false;
    return {
      label: ev.message ?? "Resposta recebida",
      icon: isError ? XCircle : CheckCircle2,
      color: isError ? "var(--nd-orange-pun)" : "#3d7d3d",
    };
  }
  if (t === "agent.text") {
    return {
      label: ev.message ?? "Escrevendo",
      icon: PenLine,
      color: "var(--wf-ink)",
    };
  }
  if (t === "agent.thinking") {
    return {
      label: ev.message ?? "Pensando…",
      icon: Brain,
      color: "var(--wf-ink-2)",
    };
  }
  if (t === "agent.error") {
    return {
      label: ev.message ?? "Erro do agente",
      icon: AlertTriangle,
      color: "var(--nd-orange-pun)",
    };
  }

  // Eventos lifecycle do heartbeat (legado, antes do parser)
  if (t.includes("started") || t.includes("invoke") || t.includes("lifecycle")) {
    return {
      label: ev.message ?? "agente iniciou",
      icon: Loader2,
      color: "var(--nd-orange)",
    };
  }
  if (t.includes("succeeded") || t.includes("completed") || t.includes("finish")) {
    return {
      label: ev.message ?? "agente concluiu",
      icon: CheckCircle2,
      color: "#3d7d3d",
    };
  }
  if (t.includes("fail") || t.includes("error")) {
    return {
      label: ev.message ?? "erro",
      icon: AlertTriangle,
      color: "var(--nd-orange-pun)",
    };
  }
  return {
    label: ev.message ?? ev.eventType,
    icon: Sparkles,
    color: "var(--wf-ink-3)",
  };
}

/** Retorna classificação do último evento (pra embed em outros componentes) */
export function lastEventLabel(events: LoggedEvent[]): string | null {
  const last = events[events.length - 1];
  return last ? classifyEvent(last).label : null;
}

export function HeartbeatEventLog({
  events,
  isWorking,
  emptyHint,
  maxHeight = 480,
}: {
  events: LoggedEvent[];
  isWorking: boolean;
  emptyHint?: string;
  maxHeight?: number;
}) {
  // mais recente no topo
  const reversed = events.slice().reverse();

  return (
    <Card padding={0}>
      <div
        style={{
          padding: "12px 18px",
          borderBottom: "1px solid var(--wf-ink-5)",
          display: "flex",
          alignItems: "center",
          gap: 8,
        }}
      >
        <Activity size={14} color="var(--nd-orange)" />
        <span
          style={{
            fontSize: 11,
            fontWeight: 600,
            color: "var(--wf-ink-2)",
            textTransform: "uppercase",
            letterSpacing: "0.06em",
          }}
        >
          {events.length === 0
            ? "Aguardando primeiro evento…"
            : `${events.length} ${events.length === 1 ? "evento" : "eventos"} do agente`}
        </span>
      </div>
      {reversed.length === 0 ? (
        <div
          style={{
            padding: 28,
            textAlign: "center",
            color: "var(--wf-ink-3)",
            fontSize: 13,
          }}
        >
          {isWorking ? (
            <>
              <Loader2
                size={20}
                strokeWidth={1.75}
                color="var(--nd-orange)"
                className="wf-spin"
                style={{ marginBottom: 8 }}
              />
              <div>{emptyHint ?? "O agente está acordando. Pode levar alguns segundos."}</div>
            </>
          ) : (
            <>Nenhum evento registrado para essa tarefa ainda.</>
          )}
        </div>
      ) : (
        <ol
          style={{
            listStyle: "none",
            padding: 0,
            margin: 0,
            maxHeight,
            overflow: "auto",
          }}
        >
          <AnimatePresence initial={false}>
            {reversed.map((ev, i) => {
              const meta = classifyEvent(ev);
              const Icon = meta.icon;
              return (
                <motion.li
                  key={ev.id}
                  initial={{ opacity: 0, x: -8 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.2 }}
                  style={{
                    display: "flex",
                    gap: 12,
                    padding: "10px 18px",
                    borderBottom:
                      i < reversed.length - 1
                        ? "1px solid var(--wf-ink-5)"
                        : "none",
                    alignItems: "flex-start",
                  }}
                >
                  <span
                    style={{
                      width: 22,
                      height: 22,
                      borderRadius: 6,
                      background: `${meta.color}1a`,
                      color: meta.color,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                      marginTop: 2,
                    }}
                  >
                    <Icon size={12} strokeWidth={2} />
                  </span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        fontSize: 13,
                        color: "var(--wf-ink)",
                        lineHeight: 1.45,
                        wordBreak: "break-word",
                      }}
                    >
                      {meta.label}
                    </div>
                    <div
                      style={{
                        fontSize: 11,
                        color: "var(--wf-ink-3)",
                        marginTop: 2,
                        display: "flex",
                        gap: 8,
                        alignItems: "center",
                        fontVariantNumeric: "tabular-nums",
                      }}
                    >
                      <span>{formatHHMMSS(ev.createdAt)}</span>
                      <span style={{ opacity: 0.6 }}>·</span>
                      <span style={{ fontFamily: "monospace", fontSize: 10 }}>
                        {ev.eventType}
                      </span>
                    </div>
                  </div>
                </motion.li>
              );
            })}
          </AnimatePresence>
        </ol>
      )}
    </Card>
  );
}
